import React from "react";
import { Inline, Stack, Card, CardContent, CardHeader, Divider, List, ListItem, ListItemIcon, ListItemText, Field} from "@digitalreality/ui";
import { useAppState } from "../../StateStore/Store";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Controller } from "react-hook-form";
import { ProjectDailyJob } from "../../Models/ProjectDailyJobs";
import dayjs, { Dayjs } from 'dayjs';

export default function DailyJobs({ onSubmit, formRef, editMode = true }: any) {
    const { t } = useTranslation();
    const { state, dispatch } = useAppState();

    const { control, handleSubmit } = useForm({
        defaultValues: state.projectModal.project,
        mode: "onBlur",
    });

    var disableDailyJob = false;
    var disableTrackingJob = false;

    const generateCronExpression = (time: Dayjs): string => {
        const minute = time.minute();
        const hour = time.hour();
        return `0 ${minute} ${hour} ? * *`; // Generates a cron expression with minute and hour
    };

    const addDailyJob = (dailyJob: ProjectDailyJob, index: number) => {
        if (dailyJob.type != 9 && dailyJob.type != 10 && dailyJob.type != 11) {
            return
        }

        // Based on licenses, only one job should be editable. After the 1st added job, disable the rest
        const disabled = disableDailyJob;
        disableDailyJob = true

        return (
            <ListItem sx={{ paddingBottom: '20px', paddingLeft: 0, paddingRight: 0}}>
                <Controller
                    name={`dailyJobs.${index}.name`}
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                        <Field
                            {...field}
                            sx={{ width: "310px", paddingRight: "10px" }}
                            label={t("Title")}
                            inputRef={ref}
                            disabled={disabled}/>
                    )}/>
                <Controller
                    name={`dailyJobs.${index}.cronexpression`}
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <TimePicker
                            sx={{ width: "200px", paddingRight: "10px" }}
                            label={t("Execution time")}
                            disabled={disabled}
                            ampm={false}
                            value={value ? dayjs(`${value.split(' ')[2]}:${value.split(' ')[1]}`, 'HH:mm') : null}
                            onChange={(newTime: Dayjs | null) => {
                                if (newTime) {
                                    const cronExpression = generateCronExpression(newTime);
                                    onChange(cronExpression); // Store the cron expression in the form value
                                } else {
                                    onChange(''); // Clear the form value if no time is selected
                                }
                            }}/>
                    )}/>
                <Box>
                <FormControl sx={{ width: "100px" }} disabled={disabled}>
                    <InputLabel>Duration</InputLabel>
                    <Controller
                        name={`dailyJobs.${index}.type`}
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                            value={value}
                            onChange={onChange}
                            label="Days back">
                            <MenuItem value={9}>1 Day</MenuItem>
                            <MenuItem value={10}>3 Day</MenuItem>
                            <MenuItem value={11}>7 Days</MenuItem>
                        </Select>
                        )}/>
                </FormControl>
                </Box>
            </ListItem>
        );
    };

    const addTrackingJob = (dailyJob: ProjectDailyJob, index: number) => {
        if (dailyJob.type != 12) {
            return
        }

        // Based on licenses, only one job should be editable. After the 1st added job, disable the rest
        const disabled = disableTrackingJob;
        disableTrackingJob = true;

        return (
            <ListItem sx={{ paddingBottom: '20px', paddingLeft: 0, paddingRight: 0}}>
                <Controller
                    name={`dailyJobs.${index}.name`}
                    control={control}
                    render={({ field: { ref, ...field } }) => (
                        <Field
                            {...field}
                            sx={{ width: "310px", paddingRight: "10px" }}
                            label={t("Title")}
                            disabled={disabled}
                            inputRef={ref}/>
                    )}/>
                <Field
                    sx={{ width: "150px", paddingRight: "10px" }}
                    label={t("Frequency")}
                    value={t("Every 4 hours")}
                    disabled={true}/>
            </ListItem>
        );
    };

    return (
        <form
            id="dailyJobsForm"
            onSubmit={handleSubmit(onSubmit)}
            ref={formRef}
            autoComplete="off">
            <Stack gap={10}>
                <Inline sx={{ width: "1292px", overflow: 'auto'}} gap={2}>
                    <Card sx={{ height: "538px", width: "650px", p: 0}}>
                        <CardHeader title="Import Snow Data"/>
                        <Divider />
                        <CardContent sx={{ height: "490px", maxHeight: "490px", overflow: 'auto'}}>
                            <List>
                                <ListItem sx={{ paddingLeft: '0', paddingBottom: '20px'}}>
                                    <ListItemText>{"Set the time when a daily job importing snow data should run (Project/local time)."}</ListItemText>
                                </ListItem>
                                {state.projectModal.project?.dailyJobs?.map((dailyJob, index) => { return addDailyJob(dailyJob, index) })}
                            </List>
                        </CardContent>
                    </Card>
                    <Card sx={{ height: "538px", width: "650px", p: 0}}>
                        <CardHeader title="Import Vehicle Tracking Data"/>
                        <Divider />
                        <CardContent sx={{ height: "490px", maxHeight: "490px", overflow: 'auto'}}>
                            <List>
                                <ListItem sx={{ paddingLeft: '0', paddingBottom: '20px'}}>
                                    <ListItemText>{"Set how often the vehicle tracking data should be imported."}</ListItemText>
                                </ListItem>
                                {state.projectModal.project?.dailyJobs?.map((dailyJob, index) => { return addTrackingJob(dailyJob, index) })}
                            </List>
                        </CardContent>
                    </Card>
                </Inline>
            </Stack>
        </form>
    );
}